import React, { useState, useEffect } from "react";
import { User, Mail, Phone, FileText, Building2, Calendar, Award} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";

const ResidenceInfomation = ({ residenceId }) => {
  const [manager, setManager] = useState({
    name: "",
    email: "",
    phone: "",
    bio: "",
    image: ""
  });

  // Fetch manager data from backend
  useEffect(() => {
    console.log("ResidenceInfomation mounted with residenceId:", residenceId);
    if (!residenceId) return;

    const fetchManager = async () => {
      console.log("Fetching manager for residenceId:", residenceId);
      try {
        const res = await fetch(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/manager/residence/${residenceId}`);
        if (!res.ok) throw new Error("Failed to fetch manager");
        const data = await res.json();
        setManager({
          name: data.name || "",
          email: data.email || "",
          phone: data.phone || "",
          bio: data.bio || "",
          image: data.image || "uploads/managers/default.png"
        });
      } catch (err) {
        console.error("Error fetching manager:", err);
        toast.error("Failed to load manager data");
      }
    };

    fetchManager();
  }, [residenceId]);

  // Determine image URL to display
  const getImageUrl = () => {
    if (!manager.image) return "https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/uploads/managers/default.png";
    return manager.image.startsWith("uploads/")
      ? `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/${manager.image}`
      : manager.image;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <Toaster position="top-right" />

      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-lg">
            <Building2 className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-700">Manager Information</h2>
            <p className="text-sm text-gray-500 mt-0.5">Residence manager details and contact information</p>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3">
        {/* LEFT PANEL – Profile */}
        <div className="border-r border-gray-200 p-6 bg-gray-50">
          <div className="flex flex-col items-center text-center">
            <div className="relative w-28 h-28 rounded-full overflow-hidden border-2 border-gray-200 bg-white shadow-sm">
              <img
                src={getImageUrl()}
                alt="Manager"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = "https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/uploads/managers/default.png";
                }}
              />
            </div>

            <h3 className="mt-4 text-base font-semibold text-gray-700">{manager.name || "Not Assigned"}</h3>
            <p className="text-sm text-gray-500">Residence Manager</p>

            <div className="mt-5 w-full pt-4 border-t border-gray-200">
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 justify-center">
                  <Mail className="w-3.5 h-3.5 text-blue-500" />
                  <span className="text-xs text-gray-600">{manager.email || "No email provided"}</span>
                </div>
                <div className="flex items-center gap-2 justify-center">
                  <Phone className="w-3.5 h-3.5 text-green-600" />
                  <span className="text-xs text-gray-600">{manager.phone || "No phone provided"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL – Info Display */}
        <div className="lg:col-span-2 p-6">
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Personal Information
            </h4>
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500" />
                  <input
                    type="text"
                    value={manager.name}
                    readOnly
                    className="w-full pl-10 pr-3 py-2 text-sm rounded-lg border border-gray-200 bg-gray-50 text-gray-700 outline-none cursor-default"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500" />
                  <input
                    type="email"
                    value={manager.email}
                    readOnly
                    className="w-full pl-10 pr-3 py-2 text-sm rounded-lg border border-gray-200 bg-gray-50 text-gray-700 outline-none cursor-default"
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Contact Number</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-600" />
                  <input
                    type="text"
                    value={manager.phone}
                    readOnly
                    className="w-full pl-10 pr-3 py-2 text-sm rounded-lg border border-gray-200 bg-gray-50 text-gray-700 outline-none cursor-default"
                  />
                </div>
              </div>

              {/* Bio */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Biography</label>
                <div className="relative">
                  <FileText className="absolute left-3 top-3 w-4 h-4 text-purple-500" />
                  <textarea
                    rows="4"
                    value={manager.bio || "No biography provided."}
                    readOnly
                    className="w-full pl-10 pr-3 py-2 text-sm rounded-lg border border-gray-200 bg-gray-50 text-gray-700 outline-none cursor-default resize-none"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Additional Info Section */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-blue-50 rounded-md">
                  <Calendar className="w-3.5 h-3.5 text-blue-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Since</p>
                  <p className="text-xs font-medium text-gray-600">2024</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-green-50 rounded-md">
                  <Award className="w-3.5 h-3.5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <p className="text-xs font-medium text-green-600">Active</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResidenceInfomation;